import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import {
  Check,
  CheckCircle2,
  Eye,
  Handshake,
  Heart,
  Home,
  KeyRound,
  Lock,
  Plane,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  UsersRound,
} from 'lucide-react';
import {
  ButtonLink,
  CardGrid,
  CheckList,
  Container,
  FeatureCard,
  FeatureRow,
  Lead,
  PageHero,
  Reveal,
  Section,
  SectionHeader,
  Steps,
} from '../components/page';
import { Avatar, Glow } from '../components/ui';
import SmartLink from '../components/SmartLink';

/* ------------------------------------------------------------------ */
/* Interactive rule picker                                             */
/* ------------------------------------------------------------------ */

const MEMBERS = [
  { initials: 'JD', color: 'blue', key: true },
  { initials: 'KS', color: 'green' },
  { initials: 'MR', color: 'amber' },
  { initials: 'SM', color: 'purple', key: true },
  { initials: 'AL', color: 'pink' },
];

// `approvers` = indexes of members whose sign-off the rule needs.
const RULES = [
  { id: 'everyone', icon: UsersRound, name: 'Everyone Agrees', approvers: [0, 1, 2, 3, 4] },
  { id: 'most', icon: Users, name: 'Most Agree', approvers: [0, 1, 2] },
  { id: 'pair', icon: Handshake, name: 'Pair Approves', approvers: [0, 1] },
  { id: 'key', icon: KeyRound, name: 'Key Voters', approvers: [0, 3] },
];

const RulePicker = () => {
  const [ruleId, setRuleId] = useState('most');
  const rule = RULES.find((r) => r.id === ruleId);

  return (
    <div className="relative">
      <Glow className="-inset-8 bg-brand-500/15" />
      <div className="surface relative !bg-ink-900/90 p-6 sm:p-8">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/[0.06] text-white">
            <SlidersHorizontal className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-white">Group Rules · Group Controls</p>
            <p className="text-xs text-slate-500">Group Trip Fund · Hawaii 2025</p>
          </div>
        </div>

        <div role="radiogroup" aria-label="Approval rule" className="mt-8 grid grid-cols-2 gap-2">
          {RULES.map(({ id, icon: Icon, name }) => {
            const selected = id === ruleId;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setRuleId(id)}
                className={clsx(
                  'relative flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-3 text-left text-sm font-semibold transition-colors duration-200',
                  selected
                    ? 'border-brand-400/50 bg-brand-400/10 text-white'
                    : 'border-white/[0.08] bg-white/[0.02] text-slate-400 hover:border-white/20 hover:text-white'
                )}
              >
                <Icon className={clsx('h-4 w-4 shrink-0', selected ? 'text-brand-300' : 'text-slate-500')} />
                {name}
                {selected && (
                  <motion.span
                    layoutId="rule-check"
                    className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-brand-400 text-ink-950"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  >
                    <Check className="h-3 w-3" strokeWidth={4} />
                  </motion.span>
                )}
              </button>
            );
          })}
        </div>

        <div className="surface-inset mt-6 p-5">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-white">Who needs to approve</span>
            <span className="tabular-nums text-slate-400">
              <span className="font-semibold text-brand-300">{rule.approvers.length}</span> of {MEMBERS.length}
            </span>
          </div>
          <div className="mt-5 grid grid-cols-5 gap-2">
            {MEMBERS.map((m, i) => {
              const needed = rule.approvers.includes(i);
              return (
                <div key={m.initials} className="flex flex-col items-center gap-2">
                  <motion.div
                    animate={{ opacity: needed ? 1 : 0.3, scale: needed ? 1 : 0.92 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 24 }}
                    className={clsx(
                      'relative rounded-full p-[3px] transition-colors duration-300',
                      needed ? 'bg-brand-400 shadow-[0_0_20px_-4px_rgba(0,211,255,0.7)]' : 'bg-white/10'
                    )}
                  >
                    <Avatar initials={m.initials} color={m.color} />
                    {ruleId === 'key' && m.key && (
                      <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-ink-950 ring-2 ring-ink-900">
                        <KeyRound className="h-3 w-3" strokeWidth={3} />
                      </span>
                    )}
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-5 h-12" aria-live="polite">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={ruleId}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
              className="flex h-full items-center gap-2 rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.07] px-4 text-sm font-medium text-emerald-300"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              {rule.name} applied to every withdrawal
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Copy                                                                */
/* ------------------------------------------------------------------ */

const WHY = [
  { icon: Lock, title: 'Feel Secure', text: 'Set rules to prevent unauthorized spending, keeping your funds safe.' },
  { icon: SlidersHorizontal, title: 'Stay in Control', text: 'Choose who approves withdrawals, from everyone to a select few.' },
  { icon: Handshake, title: 'Build Trust', text: 'Transparent rules mean everyone knows what to expect, reducing misunderstandings.' },
];

const HOW = [
  { title: 'Create Your Group', text: 'Start a group for your goal (e.g., a vacation, shared bills).' },
  { title: 'Go to Group Rules', text: 'Find the "Group Controls" option in your group settings.' },
  { title: 'Pick Your Rule', text: 'Choose who needs to approve withdrawals.' },
  { title: 'Start Spending Safely', text: "Withdrawals follow your rule, ensuring everyone's on the same page." },
];

const EXAMPLES = [
  {
    icon: Home,
    accent: 'emerald',
    title: 'Roommates Sharing Bills',
    text: 'Set "Most Agree" so the majority of housemates approve utility payments, ensuring fairness.',
  },
  {
    icon: Plane,
    accent: 'amber',
    title: 'Friends Planning a Trip',
    text: 'Use "Key Voters" to let the trip organizer and one other member approve flight bookings, keeping things efficient.',
  },
  {
    icon: Heart,
    accent: 'pink',
    title: 'Family Emergency Fund',
    text: 'Choose "Everyone Agrees" to make sure all family members are on board before dipping into savings.',
  },
];

const LOVE = [
  { icon: ShieldCheck, title: 'Peace of Mind', text: "Know your money won't be spent without the right approvals." },
  { icon: SlidersHorizontal, title: 'Customizable Options', text: "Tailor rules to match your group's dynamic—strict or relaxed, it's up to you." },
  { icon: Eye, title: 'Transparency First', text: "Everyone sees the rules upfront, so there's no room for surprises." },
  {
    icon: Handshake,
    title: 'Conflict Prevention',
    text: (
      <>
        Clear approval settings reduce disputes, and if issues arise, our{' '}
        <SmartLink to="/resolution-center" className="font-medium text-brand-300 hover:text-white">
          "Resolution Center"
        </SmartLink>{' '}
        is here to mediate.
      </>
    ),
  },
];

const GET_STARTED = [
  { title: 'Sign Up for Free', text: 'Create your Groupmoney account in minutes.' },
  { title: 'Start a Group', text: 'Invite members and set your goal.' },
  { title: 'Set Your Group Controls', text: 'Choose the approval rule that works for your group.' },
];

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const GroupControlsPage = () => (
  <>
    <PageHero
      highlight="Group Controls"
      title="You Decide How Your Group Spends"
      description="Transparent rules and powerful controls for peace of mind."
    />

    <Section className="!pt-8 sm:!pt-12">
      <Container>
        <SectionHeader title="Security and Control, Right at Your Fingertips" />
        <Lead className="mt-8">
          At Groupmoney, we understand that trust is everything when pooling money with a group. That's why we've built
          Group Controls—a powerful feature that lets you decide exactly how withdrawals happen. Whether you're saving
          with roommates, planning a trip with friends, or managing a family fund, Group Controls gives you the security
          and confidence to know your money is handled the way you want.
        </Lead>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Why It Matters"
          title="Why Group Controls Matters"
          description="When you're pooling money, the last thing you want is uncertainty about how it's spent. Group Controls puts you in the driver's seat, ensuring every withdrawal aligns with your group's agreement. Here's why it's a game-changer:"
        />
        <CardGrid cols={3} className="mt-16 sm:mt-20">
          {WHY.map(({ icon, title, text }) => (
            <FeatureCard key={title} icon={icon} title={title}>
              {text}
            </FeatureCard>
          ))}
        </CardGrid>
        <Lead className="mt-16">
          With Group Controls, you're not just pooling money—you're protecting your group's harmony.
        </Lead>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Simple Setup"
          title="How It Works"
          description="Setting up Group Controls is simple and flexible, so it fits your group's unique needs:"
        />
        <Steps steps={HOW} className="mt-16 sm:mt-20" />

        <div className="mt-24 sm:mt-32">
          <FeatureRow icon={KeyRound} eyebrow="Approval Rules" title="Rule Options Available" visual={<RulePicker />}>
            <CheckList
              className="text-base"
              items={[
                { label: 'Everyone Agrees', text: 'All members must consent before money is spent.' },
                { label: 'Most Agree', text: 'Over half the group gives the green light.' },
                { label: 'Pair Approves', text: 'Just two members (e.g., you and one other) need to agree.' },
                {
                  label: 'Key Voters',
                  text: 'Select specific members to decide, with options like "One Key Voter," "Most Key Voters," or "All Key Voters" if more than one is chosen.',
                },
              ]}
            />
            <p>
              Whether you're a cautious group needing full consensus or a flexible one trusting a few key members, Group
              Controls has you covered.
            </p>
          </FeatureRow>
        </div>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Real Groups"
          title="Real-Life Examples"
          description="Here's how Group Controls works for different groups:"
        />
        <CardGrid cols={3} className="mt-16 sm:mt-20">
          {EXAMPLES.map(({ icon, accent, title, text }) => (
            <FeatureCard key={title} icon={icon} accent={accent} title={title}>
              {text}
            </FeatureCard>
          ))}
        </CardGrid>
        <Lead className="mt-16">
          No matter your group's size or goal, Group Controls adapts to keep things secure and smooth.
        </Lead>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader eyebrow="The Difference" title="Why You'll Love It" />
        <CardGrid cols={4} className="mt-16 sm:mt-20">
          {LOVE.map(({ icon, title, text }) => (
            <FeatureCard key={title} icon={icon} title={title}>
              {text}
            </FeatureCard>
          ))}
        </CardGrid>
        <Lead className="mt-16">
          Group Controls isn't just a feature—it's your assurance that group finances stay fair, secure, and drama-free.
        </Lead>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Get Started"
          title="Get Started with Group Controls Today"
          description="Ready to take control of your group's spending? It's easy to set up:"
        />
        <Steps steps={GET_STARTED} className="mt-16 sm:mt-20" />
        <Reveal className="mt-20 text-center">
          <p className="text-xl text-slate-300">
            Join thousands of groups already using Group Controls to manage their money with confidence.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <ButtonLink to="/#get-started">Sign Up Now</ButtonLink>
            <ButtonLink to="/resolution-center" variant="secondary">
              Learn More
            </ButtonLink>
          </div>
          <p className="mt-8 text-sm text-slate-500">
            Have questions? Visit the{' '}
            <SmartLink to="/resolution-center" className="font-medium text-brand-300 hover:text-white">
              Resolution Center
            </SmartLink>{' '}
            or email us at{' '}
            <a href="mailto:support@groupmoney.app" className="font-medium text-brand-300 hover:text-white">
              support@groupmoney.app
            </a>
            .
          </p>
        </Reveal>
      </Container>
    </Section>
  </>
);

export default GroupControlsPage;
